import { Badge } from '@shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FulfillmentData,
  OwnBoxPackaging,
  PackagingRateItemData,
  RateItem,
  RateItemType,
  ShippingData,
  StorageData,
} from '../../models/rate.types';
import { RateItemDataFallback } from '../feedback/RateItemDataFallback';
import { ShippingFilters } from '../interaction/ShippingFilters';
import { FulfillmentDataDisplay } from './FulfillmentDataDisplay';
import { PackagingDataDisplay } from './PackagingDataDisplay';
import { ShippingDataDisplay } from './ShippingDataDisplay';
import { StorageDataDisplay } from './StorageDataDisplay';
import { useActiveOwnBoxes } from '../../hooks/useActiveOwnBoxes';

// Temporary item type for creation (without id from API)
export interface TempRateItem extends Omit<RateItem, 'id'> {
  id: number | string; // Can be temporary id
}

interface EditableRateItemsTableProps {
  items: TempRateItem[];
  onRemoveItem: (id: number | string) => void;
  onEditItem?: (item: TempRateItem) => void;
}

const TypeBadge = ({ type }: { type: RateItemType }) => {
  const { t } = useTranslation('ratesModule');

  const config = useMemo(() => {
    switch (type) {
      case 'shipping':
        return {
          label: t('itemTypes.shipping'),
          className: 'bg-bg-info text-info',
        };
      case 'fulfillment':
        return {
          label: t('itemTypes.fulfillment'),
          className: 'bg-bg-warning text-warning',
        };
      case 'storage':
        return {
          label: t('itemTypes.storage'),
          className: 'bg-bg-success text-success',
        };
      case 'packaging':
        return {
          label: t('itemTypes.packaging'),
          className: 'bg-bg-info text-info',
        };
      default:
        return { label: type, className: 'bg-card-border text-text' };
    }
  }, [type, t]);

  return <Badge className={`text-xs ${config.className}`}>{config.label}</Badge>;
};

const RateItemDataRenderer = ({ item, ownBoxes = [] }: { item: TempRateItem; ownBoxes?: OwnBoxPackaging[] }) => {
  if (!item.data || typeof item.data !== 'object') {
    return <RateItemDataFallback data={item.data} />;
  }

  switch (item.type) {
    case 'shipping':
      return <ShippingDataDisplay data={item.data as ShippingData} />;
    case 'fulfillment':
      return <FulfillmentDataDisplay data={item.data as FulfillmentData} />;
    case 'storage':
      return <StorageDataDisplay data={item.data as StorageData} />;
    case 'packaging':
      return <PackagingDataDisplay data={item.data as PackagingRateItemData} ownBoxes={ownBoxes} />;
    default:
      return <RateItemDataFallback data={item.data} />;
  }
};

const ShippingContext = ({ item }: { item: TempRateItem }) => {
  if (item.type !== 'shipping') return null;

  // Prefer direct name fields from API, fallback to nested objects
  const courier = item.courier_name || item.courier?.name;
  const service = item.courier_service_name || item.courier_service?.name;
  const country = item.country_name || item.country?.name;
  const zone = item.courier_zone_name || item.courier_zone?.name;

  if (!courier && !service && !country && !zone) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
      {courier && (
        <div className="flex items-center gap-1.5">
          <span className="text-subtext">Mensajería:</span>
          <span className="font-medium text-title">{courier}</span>
        </div>
      )}
      {service && (
        <div className="flex items-center gap-1.5">
          <span className="text-subtext">Servicio:</span>
          <span className="font-medium text-title">{service}</span>
        </div>
      )}
      {country && (
        <div className="flex items-center gap-1.5">
          <span className="text-subtext">País:</span>
          <span className="font-medium text-title">{country}</span>
        </div>
      )}
      {zone && (
        <div className="flex items-center gap-1.5">
          <span className="text-subtext">Zona:</span>
          <span className="font-medium text-title">{zone}</span>
        </div>
      )}
    </div>
  );
};

export const EditableRateItemsTable = ({ items, onRemoveItem, onEditItem }: EditableRateItemsTableProps) => {
  const { t } = useTranslation('ratesModule');
  const { ownBoxes } = useActiveOwnBoxes();

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCourier, setSelectedCourier] = useState('all');
  const [selectedService, setSelectedService] = useState('all');

  // Separate items by type for better organization
  const shippingItems = useMemo(() => items.filter((item) => item.type === 'shipping'), [items]);
  const fulfillmentItems = useMemo(() => items.filter((item) => item.type === 'fulfillment'), [items]);
  const storageItems = useMemo(() => items.filter((item) => item.type === 'storage'), [items]);
  const packagingItems = useMemo(() => items.filter((item) => item.type === 'packaging'), [items]);

  // Extract unique values for filters
  const availableCountries = useMemo(() => {
    const countries = new Set<string>();
    shippingItems.forEach((item) => {
      const country = item.country_name || item.country?.name;
      if (country) countries.add(country);
    });
    return Array.from(countries).sort();
  }, [shippingItems]);

  const availableCouriers = useMemo(() => {
    const couriers = new Set<string>();
    shippingItems.forEach((item) => {
      const courier = item.courier_name || item.courier?.name;
      if (courier) couriers.add(courier);
    });
    return Array.from(couriers).sort();
  }, [shippingItems]);

  const availableServices = useMemo(() => {
    const services = new Set<string>();
    shippingItems.forEach((item) => {
      const service = item.courier_service_name || item.courier_service?.name;
      if (service) services.add(service);
    });
    return Array.from(services).sort();
  }, [shippingItems]);

  // Filter shipping items
  const filteredShippingItems = useMemo(() => {
    return shippingItems.filter((item) => {
      // Search filter (by title)
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const titleMatch = item.title.toLowerCase().includes(searchLower);
        if (!titleMatch) return false;
      }

      // Country filter
      if (selectedCountry !== 'all') {
        const country = item.country_name || item.country?.name;
        if (country !== selectedCountry) return false;
      }

      // Courier filter
      if (selectedCourier !== 'all') {
        const courier = item.courier_name || item.courier?.name;
        if (courier !== selectedCourier) return false;
      }

      // Service filter
      if (selectedService !== 'all') {
        const service = item.courier_service_name || item.courier_service?.name;
        if (service !== selectedService) return false;
      }

      return true;
    });
  }, [shippingItems, searchTerm, selectedCountry, selectedCourier, selectedService]);

  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm.trim() !== '' || selectedCountry !== 'all' || selectedCourier !== 'all' || selectedService !== 'all'
    );
  }, [searchTerm, selectedCountry, selectedCourier, selectedService]);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCountry('all');
    setSelectedCourier('all');
    setSelectedService('all');
  }, []);

  const hasShipping = shippingItems.length > 0;
  const hasOtherConcepts = fulfillmentItems.length > 0 || storageItems.length > 0;
  const hasPackaging = packagingItems.length > 0;

  // Determine default tab based on available content
  const defaultTab = hasShipping ? 'shipping' : hasPackaging ? 'packaging' : 'other';

  // Early return after all hooks
  if (items.length === 0) {
    return <div className="text-center py-8 text-subtext">{t('detail.noItems')}</div>;
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="mb-6">
          {hasShipping && (
            <TabsTrigger value="shipping">
              {t('itemTypes.shipping')} ({filteredShippingItems.length})
            </TabsTrigger>
          )}
          {hasOtherConcepts && (
            <TabsTrigger value="other">
              {t('tabs.otherConcepts')} ({fulfillmentItems.length + storageItems.length})
            </TabsTrigger>
          )}
          {hasPackaging && (
            <TabsTrigger value="packaging">
              {t('tabs.packaging')} ({packagingItems.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Shipping Tab */}
        {hasShipping && (
          <TabsContent value="shipping">
            <ShippingFilters
              search={searchTerm}
              country={selectedCountry}
              courier={selectedCourier}
              service={selectedService}
              onSearchChange={setSearchTerm}
              onCountryChange={setSelectedCountry}
              onCourierChange={setSelectedCourier}
              onServiceChange={setSelectedService}
              onClearFilters={handleClearFilters}
              hasActiveFilters={hasActiveFilters}
              availableCountries={availableCountries}
              availableCouriers={availableCouriers}
              availableServices={availableServices}
            />

            {filteredShippingItems.length === 0 ? (
              <div className="text-center py-8 text-subtext">
                {hasActiveFilters ? t('filters.shipping.noResults') : t('detail.noItems')}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredShippingItems.map((item) => (
                  <div
                    key={item.id}
                    className="border border-card-border rounded-lg p-4 hover:border-primary/50 transition-colors relative group cursor-pointer"
                    onClick={() => onEditItem?.(item)}
                  >
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {onEditItem && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditItem(item);
                          }}
                          className="h-7 w-7 p-0 text-subtext hover:text-primary hover:bg-primary/10"
                        >
                          <FontAwesomeIcon icon={faPenToSquare} className="w-3 h-3" />
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveItem(item.id);
                        }}
                        className="h-7 w-7 p-0 text-error hover:text-error hover:bg-error/10"
                      >
                        <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="mb-3">
                      <ShippingContext item={item} />
                    </div>

                    <div className="mb-2">
                      <RateItemDataRenderer item={item} ownBoxes={ownBoxes} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* Other Concepts Tab (Fulfillment + Storage) */}
        {hasOtherConcepts && (
          <TabsContent value="other">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Fulfillment - Single item */}
              {fulfillmentItems.length > 0 && (
                <div
                  className="border border-card-border rounded-lg p-5 bg-card-bg/50 relative group cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => onEditItem?.(fulfillmentItems[0])}
                >
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {onEditItem && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditItem(fulfillmentItems[0]);
                        }}
                        className="h-7 w-7 p-0 text-subtext hover:text-primary hover:bg-primary/10"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} className="w-3 h-3" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveItem(fulfillmentItems[0].id);
                      }}
                      className="h-7 w-7 p-0 text-error hover:text-error hover:bg-error/10"
                    >
                      <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <TypeBadge type="fulfillment" />
                  </div>
                  {fulfillmentItems.map((item) => (
                    <div key={item.id}>
                      <div className="mb-2">
                        <RateItemDataRenderer item={item} ownBoxes={ownBoxes} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Storage - Single item */}
              {storageItems.length > 0 && (
                <div
                  className="border border-card-border rounded-lg p-5 bg-card-bg/50 relative group cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => onEditItem?.(storageItems[0])}
                >
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {onEditItem && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditItem(storageItems[0]);
                        }}
                        className="h-7 w-7 p-0 text-subtext hover:text-primary hover:bg-primary/10"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} className="w-3 h-3" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveItem(storageItems[0].id);
                      }}
                      className="h-7 w-7 p-0 text-error hover:text-error hover:bg-error/10"
                    >
                      <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <TypeBadge type="storage" />
                  </div>
                  {storageItems.map((item) => (
                    <div key={item.id}>
                      <div className="mb-2">
                        <RateItemDataRenderer item={item} ownBoxes={ownBoxes} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {hasPackaging && (
          <TabsContent value="packaging">
            {packagingItems.map((item) => (
              <div key={item.id} className="space-y-3">
                <div className="flex items-center justify-end gap-2">
                  {onEditItem && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onEditItem(item)}
                      data-testid="packaging-edit-button"
                    >
                      <FontAwesomeIcon icon={faPenToSquare} className="w-3.5 h-3.5 mr-1.5" />
                      {t('actions.editPackagingPrices')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onRemoveItem(item.id)}
                    className="text-error hover:text-error hover:bg-error/10"
                    data-testid="packaging-remove-button"
                  >
                    <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5 mr-1.5" />
                    {t('modal.create.removeItem')}
                  </Button>
                </div>
                <RateItemDataRenderer item={item} ownBoxes={ownBoxes} />
              </div>
            ))}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
